import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  Flex,
  Text,
  Button,
  Link,
  UnorderedList,
  ListItem,
  Divider,
} from '@chakra-ui/react';
import {
  SaveValidationResult,
  ValidationIssue,
} from '../utils/templateSaveValidation';
import { useUpgradeCta } from '../../../../hooks/useUpgradeCta';
import { CustomAlertCircleIcon } from '../../../../theme/icons/CustomAlertCircleIcon';
import { CustomAlertTriangleIcon } from '../../../../theme/icons/CustomAlertTriangleIcon';

interface TemplateSaveValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveAnyway: () => void;
  onIssueClick: (issue: ValidationIssue) => void;
  /**
   * Where "Edit features" goes when there is more than one issue to work
   * through. Defaults to closing, which is right for callers already sitting on
   * the builder; the template list passes a handler that opens it.
   */
  onEditFeatures?: () => void;
  onCloseComplete?: () => void;
  result: SaveValidationResult;
  isSaving?: boolean;
  mode?: 'draft' | 'publish';
}

const SectionHeading = ({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Flex align="center" gap={2} mb={2}>
    {icon}
    <Text fontWeight="600">{children}</Text>
  </Flex>
);

// Only name the actions the user can actually take — a template with no
// example-data warnings has nothing to replace.
const buildPreamble = ({
  isDraft,
  hasWarnings,
  hasErrors,
}: {
  isDraft: boolean;
  hasWarnings: boolean;
  hasErrors: boolean;
}) => {
  const lead = `This template has features that need attention before ${
    isDraft ? 'saving' : 'publishing'
  }.`;

  const actions: string[] = [];
  if (hasWarnings) actions.push('replace example data with your own');
  if (hasErrors) {
    actions.push(
      isDraft ? 'disable features' : 'disable features or upgrade your plan'
    );
  }

  if (!actions.length) return lead;

  const sentence = actions.join(', ');
  return `${lead} ${sentence[0].toUpperCase()}${sentence.slice(1)}.`;
};

const IssueItem = ({
  issue,
  onClick,
}: {
  issue: ValidationIssue;
  onClick?: () => void;
}) => (
  <ListItem>
    {onClick ? (
      <Link
        onClick={onClick}
        textDecoration="underline"
        textUnderlineOffset="3px"
        cursor="pointer"
        title={issue.description}
      >
        {issue.title}
      </Link>
    ) : (
      <Text as="span">{issue.title}</Text>
    )}
  </ListItem>
);

const TemplateSaveValidationModal = ({
  isOpen,
  onClose,
  onSaveAnyway,
  onIssueClick,
  onEditFeatures,
  onCloseComplete,
  result,
  isSaving,
  mode = 'publish',
}: TemplateSaveValidationModalProps) => {
  const { errors, warnings } = result;
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;
  const upgradeCta = useUpgradeCta();
  const isDraft = mode === 'draft';
  const canProceed = isDraft || !hasErrors;
  const proceedLabel = isDraft ? 'Save draft' : 'Publish anyway';

  const handleClick = (issue: ValidationIssue) =>
    issue.sectionId ? () => onIssueClick(issue) : undefined;

  // With a single issue there is no ambiguity about where to send the user, so
  // "Edit features" does what clicking that issue would. Past one, hand back to
  // the caller so they can work through the list on the builder.
  const allIssues = [...errors, ...warnings];
  const soleIssue =
    allIssues.length === 1 && allIssues[0].sectionId ? allIssues[0] : null;
  const handleEditFeatures = soleIssue
    ? () => onIssueClick(soleIssue)
    : (onEditFeatures ?? onClose);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      size="lg"
      isCentered
    >
      <ModalOverlay />
      <ModalContent py={6} px={4}>
        <ModalHeader>
          Some features require attention
          <Divider border="0.5px solid lightGrey" mt="15px" />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb={6}>
            {buildPreamble({ isDraft, hasWarnings, hasErrors })}
          </Text>

          {hasWarnings && (
            <Box mb={hasErrors ? 6 : 0}>
              <SectionHeading icon={<CustomAlertTriangleIcon boxSize="16px" />}>
                These sections still contain some example data
              </SectionHeading>
              <UnorderedList spacing={1} pl={5}>
                {warnings.map((w) => (
                  <IssueItem key={w.id} issue={w} onClick={handleClick(w)} />
                ))}
              </UnorderedList>
            </Box>
          )}

          {hasErrors && (
            <Box>
              <SectionHeading icon={<CustomAlertCircleIcon boxSize="16px" />}>
                Advanced features not in your plan
              </SectionHeading>
              <UnorderedList spacing={1} pl={5}>
                {errors.map((e) => (
                  <IssueItem key={e.id} issue={e} onClick={handleClick(e)} />
                ))}
              </UnorderedList>
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Flex gap={3} width="100%" align="center">
            {canProceed && (hasWarnings || (isDraft && hasErrors)) && (
              <Button
                variant="secondary"
                width="100%"
                onClick={onSaveAnyway}
                isLoading={isSaving}
              >
                {proceedLabel}
              </Button>
            )}
            {!isDraft && hasErrors && (
              <Button
                {...upgradeCta.linkProps}
                variant="secondary"
                width="100%"
              >
                {upgradeCta.label}
              </Button>
            )}
            <Button
              width="100%"
              onClick={handleEditFeatures}
              isDisabled={isSaving}
            >
              Edit features
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TemplateSaveValidationModal;
